import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';

import { OfferTranslations } from './types';
import { useCache } from './CacheContext';

interface OfferTranslationsContextType {
  offerTranslations: OfferTranslations;
  cacheTranslations: (offerId: number, translations: OfferTranslations) => void;
  getCachedTranslations: (offerId: number) => OfferTranslations | undefined;
}

const OfferTranslationsContext = createContext<
  OfferTranslationsContextType | undefined
>(undefined);

interface OfferTranslationsProviderProps {
  children: ReactNode;
  offerId?: number;
}

const createDefaultOfferTranslations = (): OfferTranslations => ({
  name: '',
  display_name: '',
  subtitle_condition_met_product_selection: '',
  subtitle_condition_not_met: '',
  title_condition_met_product_selection: '',
  title_condition_not_met: '',
  title_empty_cart: '',
  and_select_one_of_the_free_gifts: '',
  buy_any_product_from_the_collection: '',
  buy_product: '',
  buy_reqproductq_products: '',
  buy_reqproductq_products_from_the_collection: '',
  custom_free_gift: '',
  dont_miss_your_reward: '',
  free_shipping: '',
  select_your_free_gift: '',
  spend_remainingspend: '',
  spend_remainingspend_in_the_collection: '',
  spend_remainingspend_more: '',
  spend_remainingspend_more_in_the_collection: '',
  to_get_a_free_displayname: '',
  to_get_a_free_gift: '',
  to_get_free_shipping: '',
  target_shopify_collection_name: '',
});

export const OfferTranslationsProvider: React.FC<
  OfferTranslationsProviderProps
> = ({ children, offerId }) => {
  const { get: getCache, set: setCache, has: hasCache } = useCache();

  const makeTranslations = useCallback(
    (offerId?: number): OfferTranslations => {
      if (offerId && hasCache(offerId)) {
        return getCache(offerId)!;
      }

      return createDefaultOfferTranslations();
    },
    [getCache, hasCache],
  );

  const [offerTranslations, setOfferTranslations] = useState<OfferTranslations>(
    makeTranslations(offerId),
  );

  useEffect(() => {
    setOfferTranslations(makeTranslations(offerId));
  }, [offerId, makeTranslations]);

  const cacheTranslations = useCallback(
    (offerId: number, translations: OfferTranslations) => {
      setCache(offerId, translations);
    },
    [setCache],
  );

  const getCachedTranslations = useCallback(
    (offerId: number): OfferTranslations | undefined => {
      return getCache(offerId);
    },
    [getCache],
  );

  const contextValue = useMemo(
    () => ({
      offerTranslations,
      cacheTranslations,
      getCachedTranslations,
    }),
    [offerTranslations, cacheTranslations, getCachedTranslations],
  );

  return (
    <OfferTranslationsContext.Provider value={contextValue}>
      {children}
    </OfferTranslationsContext.Provider>
  );
};

export const useOfferTranslations = () => {
  const context = useContext(OfferTranslationsContext);
  if (context === undefined) {
    throw new Error(
      'useOfferTranslations must be used within an OfferTranslationsProvider',
    );
  }

  return context;
};
